/* eslint-disable react/jsx-no-useless-fragment */
/* eslint-disable no-unused-vars */
import React from 'react';
import {
  Box,
  Flex,
  useColorModeValue,
  Stack,
  Icon,
  Divider,
  Image,
  Avatar,
  Text,
  ScaleFade,
  StackProps,
} from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { SignOut, User } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import { NavItem } from '../navItem';
import { PrivateRoutes } from '../../routes/private.routes';
import logo from '../../assets/images/logo_black.png';
import { useAuth } from '../../providers/auth';
import { NavItemMenu } from '../navItemMenu';

import packageJson from '../../../package.json';

const { version } = packageJson;

interface SidebarProps extends StackProps {
  onToggle: () => void;
  isOpen: boolean;
}

const Sidebar = ({ onToggle, isOpen, ...rest }: SidebarProps) => {
  const { Logout, user } = useAuth();
  const navigate = useNavigate();

  const onClickMenuChangeSize = () => {
    onToggle();
  };

  const routeSettings = [
    {
      groupId: 1,
      name: 'Sistema',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        '/students',
        '/secretaries',
        '/institutions/list',
        '/assessments/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/topics',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
        '/templates',
        '/templates/list',
        '/templates/link',
        '/modules',
        '/notifications',
        '/users',
        '/permissions',
        '/permissions/list',
        '/permissions/logs',
        '/config',
        '/settings/general',
        '/settings/terms',
        '/settings/politics',
      ],
      redirect: '/',
    },
    {
      groupId: 2,
      name: 'admin',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        '/students',
        '/secretaries',
        '/assessments/list',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/topics',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
        '/templates',
        '/templates/list',
        '/templates/link',
        '/modules',
        '/notifications',
        '/users',
        '/permissions',
        '/permissions/list',
        '/permissions/logs',
        '/config',
        '/settings/general',
        '/settings/terms',
        '/settings/politics',
      ],
      redirect: '/',
    },
    {
      groupId: 3,
      name: 'Tutor',
      routes: [
        '/',
        '/teachers',
        '/secretaries',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
      ],
      redirect: '/',
    },
    {
      groupId: 6,
      name: 'Responsável da secretaria',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        // WEB-06: Secretário não deve ver aba "Secretarias"
        // '/secretaries',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/extraMaterials',
        '/notifications',
      ],
      redirect: '/',
    },
    {
      groupId: 7,
      name: 'Responsável da instituição',
      routes: [
        '/',
        '/teachers',
        '/tutors',
        '/studenst',
        '/institutions/list',
        '/institutions/list/:institutionId/teams',
        '/institutions/list/:institutionId/teams/:teamId/students',
        '/responsible',
        '/user/institutions',
        '/contents',
        '/contents/modules',
        '/contents/modules/:moduleId/weeks',
        '/contents/modules/:moduleId/weeks/:weekId/meetings',
        '/contents/categories',
        '/extraMaterials',
      ],
      redirect: '/',
    },
  ];
  const findUser = routeSettings.find((item) => item.groupId === user.group.id);
  return (
    <Stack
      position="fixed"
      transition="0.5s ease"
      boxShadow="0 2px 4px #00000065"
      bg="white"
      borderRight="1px solid #FAFAFA"
      w={isOpen ? '290px' : '64px'}
      h="100vh"
      overflowY="auto"
      overflowX="hidden"
      justifyContent="space-between"
      userSelect="none"
      {...rest}
    >
      <Box>
        <Flex justify="center" mb="40px">
          <Image src={logo} w={isOpen ? '100px' : '35px'} transition="0s ease" mt="50px" />
        </Flex>

        <Box position="relative" mt={isOpen ? '20px' : '60px'} zIndex={1}>
          <Flex
            w="30px"
            h="30px"
            position="absolute"
            transition="0.5s ease"
            cursor="pointer"
            borderRadius="full"
            right={isOpen ? 3 : 4}
            top={isOpen ? 3 : -10}
            bg={useColorModeValue('theme.light.tertiary', 'theme.dark.tertiary')}
            display={{ base: 'none', md: 'flex' }}
            onClick={() => onClickMenuChangeSize()}
            justify="center"
            align="center"
          >
            <Icon
              w="20px"
              h="25px"
              as={isOpen ? ChevronLeftIcon : ChevronRightIcon}
              color={useColorModeValue('text.light.tertiary', 'text.dark.tertiary')}
            />
          </Flex>
        </Box>

        <Flex
          align="center"
          pl="15px"
          pb="20px"
          cursor="pointer"
          transition="0.3s ease"
          _hover={{ opacity: 0.6 }}
        >
          <Avatar
            size={isOpen ? 'md' : 'sm'}
            src={user.image?.original}
            mr={isOpen ? '30px' : '0px'}
          />
          <ScaleFade in={isOpen} initialScale={0}>
            {isOpen && (
              <Text
                fontSize="18px"
                color={useColorModeValue('text.light.primary', 'text.dark.primary')}
              >
                {user.name}
              </Text>
            )}
          </ScaleFade>
        </Flex>

        <Flex justify="center" mb="30px">
          <Divider w="90%" />
        </Flex>
        <Box mt={isOpen ? '5%' : '40%'}>
          <Box>
            {(user.group.id === 7 || user.group.id < 7) &&
              PrivateRoutes.filter((item) => item.localeSidebar === 'top' && item.showSidebar)
                .filter((item) =>
                  findUser?.routes.find((routeByUser) => item?.path === routeByUser)
                )
                .filter((item) => item.path !== '/profile')
                .map((link) => (
                  <div key={link.name}>
                    {link.isParentMenu ? (
                      <NavItemMenu
                        icon={link.icon}
                        openMenu={isOpen}
                        parentName={link.name}
                        childrenMenus={PrivateRoutes.filter((item) =>
                          user.group.id === 6
                            ? item?.path !== '/contents/categories'
                            : item.parentMenu === link.name
                        )
                          .filter((item) => item.parentMenu === link.name)
                          .filter((item) =>
                            user.group.id === 7 && item?.path === '/institutions/list'
                              ? undefined
                              : item.parentMenu === link.name
                          )}
                      />
                    ) : (
                      <NavItem href={link.path as string} icon={link.icon} openMenu={isOpen}>
                        {link.name}
                      </NavItem>
                    )}
                  </div>
                ))}
          </Box>
        </Box>
      </Box>

      <Box pb="30px">
        <Flex
          w={isOpen ? '80%' : '95%'}
          p="35px 0 13px 15px"
          align="center"
          cursor="pointer"
          onClick={() => navigate('/profile')}
        >
          <Icon
            mr={isOpen ? '30px' : '0px'}
            w="25px"
            h="25px"
            as={User}
            color={useColorModeValue('text.light.primary', 'text.dark.primary')}
          />
          <ScaleFade in={isOpen} initialScale={0.5}>
            {isOpen && (
              <Text
                m={0}
                color={useColorModeValue('text.light.primary', 'text.dark.primary')}
                fontFamily="heading"
                fontWeight="600"
              >
                Perfil
              </Text>
            )}
          </ScaleFade>
        </Flex>
        <Flex
          w={isOpen ? '80%' : '95%'}
          p="8px 0 20px 15px"
          align="center"
          cursor="pointer"
          onClick={Logout}
        >
          <Icon
            mr={isOpen ? '30px' : '0px'}
            w="25px"
            h="25px"
            as={SignOut}
            color={useColorModeValue('text.light.primary', 'text.dark.primary')}
          />
          <ScaleFade in={isOpen} initialScale={0.5}>
            {isOpen && (
              <Text
                m={0}
                color={useColorModeValue('text.light.primary', 'text.dark.primary')}
                fontFamily="heading"
                fontWeight="600"
              >
                Sair
              </Text>
            )}
          </ScaleFade>
        </Flex>
        <Flex
          w={isOpen ? '80%' : '95%'}
          p="8px 0 20px 15px"
          align="center"
          cursor="pointer"
          onClick={() => null}
        >
          <ScaleFade in={isOpen} initialScale={0.5}>
            {isOpen && (
              <Text
                m={0}
                color={useColorModeValue('text.light.primary', 'text.dark.primary')}
                fontFamily="heading"
                fontWeight="600"
              >
                {`v${version}`}
              </Text>
            )}
          </ScaleFade>
        </Flex>
      </Box>
    </Stack>
  );
};

export default Sidebar;
